import { AccountSetting } from '@wb/core/interfaces/account-setting';
import { AnnouncementType } from '@wb/core/enums/settings';

export const snakeToCamel = (s: string): string => {
  return s.replace(/(_\w)/g, (m: string) => m[1].toUpperCase());
};

export const convertStringToNumber = (value: any): string | number => {
  if (typeof value === 'string' && !isNaN(Number(value))) {
    // if it starts with a `0`, is 5 characters long, and does not contain `.` it's a zip code, and we need to keep it as a string
    const isZeroZipCode: boolean = value.startsWith('0') && !value.includes('.') && value.length === 5;
    return isZeroZipCode ? value : Number(value);
  }
  return value;
};

export const getAccountSettings = (accountSettings: AccountSetting[], keys: string[]) => {
  const settings: any = {};

  keys.forEach((key: string): void => {
    const foundSetting: AccountSetting | undefined = accountSettings.find(
      (setting: AccountSetting): boolean => setting.key === key,
    );
    settings[snakeToCamel(key)] = convertStringToNumber(foundSetting?.value);
  });

  return settings;
};

export const parseJsonOrEmptyArray = (json: string | null | undefined) => {
  if (json) {
    try {
      return JSON.parse(json);
    } catch {}
  }

  return [];
};

export const parseJsonOrEmptyObject = (json: string | null | undefined) => {
  if (json) {
    try {
      return JSON.parse(json);
    } catch {}
  }

  return {};
};

// List of tax breakdown types from MarketScan
export const TaxBreakdownTypes = {
  0: 'Unknown',
  1: 'Custom Fee',
  2: 'Documentary Stamp Tax',
  3: 'City Tax',
  4: 'County Tax',
  5: 'Sales Tax',
  6: 'State Tax',
  7: 'State Fee',
  8: 'State Business Tax',
  9: 'State Aftermarket Tax',
  10: 'Local Tax',
  11: 'Local Business Tax',
  12: 'Local Business Aftermarket Tax',
  13: 'Local Aftermarket Tax',
  14: 'Single Article Tax',
  15: 'RTA Tax',
  16: 'Warranty Tax',
  17: 'Inventory Tax',
  18: 'Garaged State Tax',
  19: 'Registration Fee',
  20: 'Processing Fee',
  21: 'Discount Fee',
  22: 'Acquisition Fee',
  23: 'VSI Fee',
  24: 'Tire Fee',
  25: 'Supplemental Title Fee',
  26: 'Trade Tax Exemption',
  27: 'Rebate Tax Exemption',
  28: 'Sales Tax Exemption',
  29: 'Processing Fee Tax Exemption',
  30: 'Documentation Fee Tax Exemption',
  31: 'Tax Amount Exemption',
  32: 'RTD Tax',
  33: 'CAT Tax',
  34: 'BPOL Tax',
  35: 'Flat Tax',
  36: 'BANDO Tax',
  37: 'City Flat Tax',
  38: 'County Flat Tax',
  39: 'State Flat Tax',
  40: 'CAF Tax Exemption',
  41: 'Property Tax',
  42: 'PST Tax',
  43: 'GST Tax',
  44: 'Luxury Tax',
  45: 'Federal Air Conditioner Excise Tax',
  46: 'Fuel Inefficient Excise Tax',
  47: 'Price',
  48: 'AMVIC Fee',
  49: 'OMVIC Fee',
  50: 'Part Labor Tax',
  51: 'PPSA Fee',
  52: 'Lemon Law Fee',
};

export const getAnnouncementLogo = (type: string): string => {
  switch (type) {
    case AnnouncementType.CREDIT700:
      return 'https://webbuy-v2-admin-account-assets.s3.us-west-2.amazonaws.com/assets/700Credit_Logo.png';
    case AnnouncementType.PODIUM:
      return 'https://webbuy-v2-admin-account-assets.s3.us-west-2.amazonaws.com/assets/podium.png';
    default:
      return '';
  }
};

// Normalize new and editted trade adjustments for v2
export const tradeAdjustmentSlugMap = (slug: string) => {
  switch (slug?.toLowerCase()) {
    case 'mechanical':
      return 'mechanical';
    case 'body':
    case 'body/exterior':
    case 'bodyexterior':
      return 'bodyexterior';
    case 'electronics':
    case 'electrical':
      return 'electrical';
    case 'glass':
      return 'glass';
    case 'brakes':
      return 'brakes';
    case 'wheels':
    case 'wheelstires':
      return 'wheels';
    case 'interior':
      return 'interior';
    default:
      return 'mechanical';
  }
};
